import {
    AttachMoneyRounded as AttachMoneyRoundedIcon,
    Checklist as ChecklistIcon,
    ContentCopy as ContentCopyIcon,
    DriveFileMoveOutlined as DriveFileMoveOutlinedIcon,
    MoneyOffRounded as MoneyOffRoundedIcon,
    PaidRounded as PaidRoundedIcon,
    PriceChange as PriceChangeIcon,
    UpdateRounded as UpdateRoundedIcon,
} from 'components/icons';
import CancelIcon from 'assets/images/icons/CancelIcon.svg?react';
import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import { FormattedDate } from 'components/misc/FormattedDate';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal.tsx';
import SwitchMatterModal, { SwitchMatterModalResult } from 'components/modals/invoices/SwitchMatterModal.tsx';
import TimecardBulkCapFormModal from 'components/modals/invoices/TimecardBulkCapFormModal';
import TimecardBulkPriceChangeModal from 'components/modals/invoices/TimecardBulkPriceChangeModal.tsx';
import { useModal } from 'components/modals/useModal';
import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { FORM_OVERFLOW_CUSTOM_STYLE } from 'components/modals/util';
import { useCallback } from 'react';
import { UseFormGetValues } from 'react-hook-form';
import { useSelector } from 'store';
import { useDeleteInvoiceTimecardsMutation } from 'store/api/invoice-details.ts';
import { FeatureFlag } from 'types/features.ts';
import { InvoiceMatterForm, InvoiceTimecardForm } from 'types/invoice-details';
import { InvoiceData } from 'types/invoices';
import { errorAlert, successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import { ItemActionButton, ItemActionGroup } from './ItemActionButton';
import { DIALOG_448, DIALOG_480, DIALOG_560 } from 'components/modals/dialogWidths';

interface Props {
    handleMoveTimecard: (value: SwitchMatterModalResult) => Promise<void> | void;
    invoiceId: number;
    matterId: number;
    handleNonBillable: () => void;
    handleBillable: () => void;
    handlePostpone: () => void;
    handleBulkSelect: () => void;
    handleDuplicate: () => void;
    handleRemove: () => void;
    handleChangePrice: (value: number | null) => Promise<void> | void;
    selectedIds: Set<number>;
    hasBillableSelected: boolean;
    hasNonBillableSelected: boolean;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    invoiceAllowsNewTimecards: boolean;
    userCanCreateTimecards: boolean;
    isCorrectionInvoice: boolean;
    invoice: InvoiceData;
}

/** The selection's dialogs and their gates, which both designs' action bars open. */
/** A discard confirmation's list of what goes: one bordered box, a divided row each, the figures at the end. */
export const DISCARD_LIST_CLASS =
    'border-divider text-foreground mt-3 flex max-h-60 flex-col overflow-y-auto rounded-lg border';
export const DISCARD_ROW_CLASS = 'text-meta border-divider flex items-baseline gap-3 px-3 py-2 not-first:border-t';

export const useTimecardActions = (props: Props) => {
    const { handleMoveTimecard, handleChangePrice, selectedIds, getValues, invoiceId, matterId, invoice } = props;
    const { openModal, closeModal } = useModal();
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);
    const handleChange = async (value: SwitchMatterModalResult) => {
        await handleMoveTimecard(value);
        closeModal();
    };

    const isUserPrivilegedToUpdateHourlyRate = useHasPermissions(['UPDATE.INVOICES.TIMECARD_HOURLY_RATE.ALL']);

    const buttonsDisabled = useSelector((state) => state.buttons.buttonsDisabled);
    const areIconsDisabled = buttonsDisabled || selectedIds.size === 0;
    const isMultipleSelection = selectedIds.size > 1;

    const openSwitchMatterModal = (): void => {
        openModal(
            <SwitchMatterModal
                onSubmit={handleChange}
                onCancel={closeModal}
                titleId={'invoices.move-to-another-case'}
                entityIds={selectedIds}
                entitySingular={t('timecards.entity-name-singular')}
                entityPlural={t('timecards.entity-name-plural')}
                showAssignmentSelect={true}
                allowClosedAssignments
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE,
            undefined,
            DIALOG_560
        );
    };

    const openBulkCapModal = useCallback(() => {
        const selectedTimecards = ((getValues('invoiceTimecards') as InvoiceTimecardForm[]) ?? []).filter((tc) =>
            selectedIds.has(tc.id)
        );
        const initialAmountBefore = selectedTimecards.reduce((acc: number, curr: InvoiceTimecardForm) => {
            return acc + Number(curr.totalBeforeAdjustment);
        }, 0);
        const totalBilledTimeInSeconds = selectedTimecards.reduce((acc: number, curr: InvoiceTimecardForm) => {
            return acc + Number(curr.timecard?.billedTimeInSeconds ?? 0);
        }, 0);
        openModal(
            <TimecardBulkCapFormModal
                initialAmountBefore={initialAmountBefore}
                initialBilledTimeInSeconds={totalBilledTimeInSeconds}
                onClose={closeModal}
                timecards={selectedTimecards}
                invoiceId={invoiceId}
                matterId={matterId}
                isPaymentCurrencyMode={isPaymentCurrencyMode}
                currency={displayCurrency}
            />,
            closeModal,
            {
                maxWidth: '433px',
            },
            undefined,
            DIALOG_480
        );
    }, [getValues, openModal, closeModal, invoiceId, matterId, selectedIds, isPaymentCurrencyMode, displayCurrency]);

    const [deleteInvoiceTimecards] = useDeleteInvoiceTimecardsMutation();

    const openBulkPriceChangeModal = (): void => {
        openModal(
            <TimecardBulkPriceChangeModal
                onClose={closeModal}
                onChange={handleChangePrice}
                count={selectedIds.size}
                isPaymentCurrencyMode={isPaymentCurrencyMode}
                currency={displayCurrency}
            />,
            closeModal,
            {
                maxWidth: '433px',
            },
            undefined,
            DIALOG_448
        );
    };

    const openBulkDiscardConfirmationModal = (): void => {
        const discarded = ((getValues('invoiceTimecards') as InvoiceTimecardForm[]) ?? []).filter(
            (invoiceTimecard) => selectedIds.has(invoiceTimecard.id) && invoiceTimecard.timecard
        );
        const selectedTimecards = discarded.map((invoiceTimecard) => invoiceTimecard.timecard!.id);
        try {
            openModal(
                <ConfirmationModal
                    onCancel={closeModal}
                    onConfirm={() => {
                        void deleteInvoiceTimecards({ invoiceId, matterId, timecardIds: selectedTimecards })
                            .then(() => successAlert(t('invoices.details.discard-timecards-succesful')))
                            .catch((err) => {
                                console.error('Error discarding expenses:', err);
                                logError(err);
                            })
                            .finally(() => closeModal());
                    }}
                    content={<p className="text-body1">{t('invoices.details.discard-timecard-confirmation')}</p>}
                    alert={{
                        titleId: 'invoices.confirm.discard-timecards-title',
                        titleValues: { count: selectedTimecards.length },
                        description: (
                            <>
                                <p>{t('invoices.confirm.discard-timecards-text')}</p>
                                <ul className={DISCARD_LIST_CLASS}>
                                    {discarded.map((invoiceTimecard) => (
                                        <li key={invoiceTimecard.id} className={DISCARD_ROW_CLASS}>
                                            <span className="min-w-0 flex-auto">
                                                <span className="font-medium">{invoiceTimecard.employee?.name}</span>
                                                <span className="text-foreground-tertiary">
                                                    {' · '}
                                                    <FormattedDate value={invoiceTimecard.timecardDate} />
                                                    {invoiceTimecard.timecardActivityType
                                                        ? ` · ${invoiceTimecard.timecardActivityType.label}`
                                                        : null}
                                                </span>
                                            </span>
                                            <span className="text-foreground-secondary shrink-0 tabular-nums">
                                                <HoursDisplay
                                                    value={(invoiceTimecard.billedTimeInSeconds ?? 0) / 3600}
                                                />
                                                {' · '}
                                                <MoneyDisplay
                                                    amount={invoiceTimecard.totalAmount ?? 0}
                                                    currency={invoice.invoiceCurrencyTypeCode}
                                                />
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        ),
                        confirmId: 'invoices.tooltips.discard-timecards',
                        tone: 'danger',
                    }}
                />
            );
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    return {
        buttonsDisabled,
        areIconsDisabled,
        isMultipleSelection,
        isUserPrivilegedToUpdateHourlyRate,
        openSwitchMatterModal,
        openBulkCapModal,
        openBulkPriceChangeModal,
        openBulkDiscardConfirmationModal,
    };
};

const TimecardActions: React.FC<Props> = (props) => {
    const {
        handleNonBillable,
        handleBillable,
        handlePostpone,
        handleBulkSelect,
        handleDuplicate,
        handleRemove,
        hasBillableSelected,
        hasNonBillableSelected,
        invoiceAllowsNewTimecards,
        userCanCreateTimecards,
        isCorrectionInvoice,
    } = props;
    const {
        buttonsDisabled,
        areIconsDisabled,
        isMultipleSelection,
        isUserPrivilegedToUpdateHourlyRate,
        openSwitchMatterModal,
        openBulkCapModal,
        openBulkPriceChangeModal,
        openBulkDiscardConfirmationModal,
    } = useTimecardActions(props);

    return (
        <div className="flex items-center gap-2">
            <ItemActionGroup>
                <ItemActionButton
                    tooltip={t('invoices.tooltips.bulk-selection')}
                    onClick={handleBulkSelect}
                    disabled={buttonsDisabled}
                >
                    <ChecklistIcon className="text-[20px]" />
                </ItemActionButton>
            </ItemActionGroup>
            <ItemActionGroup>
                <ItemActionButton
                    tooltip={t('invoices.tooltips.postpone')}
                    disabled={areIconsDisabled}
                    onClick={handlePostpone}
                >
                    <UpdateRoundedIcon className="text-[20px]" />
                </ItemActionButton>
                <ItemActionButton
                    tooltip={t('invoices.tooltips.mark-as-non-billable')}
                    disabled={areIconsDisabled || hasNonBillableSelected}
                    onClick={handleNonBillable}
                >
                    <MoneyOffRoundedIcon className="text-[20px]" />
                </ItemActionButton>
                <ItemActionButton
                    tooltip={t('invoices.tooltips.mark-as-billable')}
                    disabled={areIconsDisabled || hasBillableSelected}
                    onClick={handleBillable}
                >
                    <AttachMoneyRoundedIcon className="text-[20px]" />
                </ItemActionButton>
                <ItemActionButton
                    tooltip={t('invoices.tooltips.change-amount-in-bulk')}
                    disabled={areIconsDisabled}
                    onClick={openBulkCapModal}
                >
                    <PaidRoundedIcon className="text-[20px]" />
                </ItemActionButton>
                {isUserPrivilegedToUpdateHourlyRate ? (
                    <ItemActionButton
                        tooltip={t('invoices.tooltips.change-price-in-bulk')}
                        disabled={areIconsDisabled}
                        onClick={openBulkPriceChangeModal}
                    >
                        <PriceChangeIcon className="text-[20px]" />
                    </ItemActionButton>
                ) : null}
                <ItemActionButton
                    tooltip={t('invoices.tooltips.move-to-another-case')}
                    disabled={areIconsDisabled}
                    onClick={openSwitchMatterModal}
                >
                    <DriveFileMoveOutlinedIcon className="text-[20px]" />
                </ItemActionButton>
                {isCorrectionInvoice ? (
                    <ItemActionButton
                        tooltip={t('invoices.tooltips.remove-via-correction')}
                        disabled={areIconsDisabled}
                        onClick={handleRemove}
                    >
                        <CancelIcon className="size-[18px]" />
                    </ItemActionButton>
                ) : (
                    <FeatureFlagged action="show" flag={FeatureFlag.DISCARD_TIMECARDS_EXPENSES_ON_INVOICE}>
                        <ItemActionButton
                            tooltip={t('invoices.tooltips.discard-timecards')}
                            disabled={areIconsDisabled}
                            onClick={openBulkDiscardConfirmationModal}
                        >
                            <CancelIcon className="size-[18px]" />
                        </ItemActionButton>
                    </FeatureFlagged>
                )}
                {invoiceAllowsNewTimecards ? (
                    <ItemActionButton
                        tooltip={t('invoices.tooltips.duplicate')}
                        disabled={areIconsDisabled || isMultipleSelection || !userCanCreateTimecards}
                        onClick={handleDuplicate}
                    >
                        <ContentCopyIcon className="text-[20px]" />
                    </ItemActionButton>
                ) : null}
            </ItemActionGroup>
        </div>
    );
};

export default TimecardActions;
